// Considered: SphereTypeSelector — cannot extend it: it gates all computation on
// perspectiveSystemNames.length > 0, sources schemas from useAvailableTypes(sphereId)
// (empty without a sphereId), is single-select, is not exported, and imports
// @center-pwa/store. Ungating it would change the shared sphere-creation path.
// Reuses its category utils (getSchemaCategories/getEligibleSchemaObjects) only —
// not the component. See spec Reuse Audit decision #8.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { getEligibleSchemaObjects, getSchemaCategories } from '@center-platform/spheres/identity';
import type { Schema } from 'types/schema';
import type { FilterExpression } from 'types/filters';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './SphereFamilyTypePickerMolecule.scss';

function filterValues(filter: FilterExpression | undefined): string[] {
  if (!filter) return [];
  if (Array.isArray(filter.values)) return filter.values as string[];
  if (Array.isArray(filter.value)) return filter.value as string[];
  if (typeof filter.value === 'string' && filter.value) return [filter.value];
  return [];
}

/** Walk `parent_schema_id` up from `schema`; true if `ancestorSchemaId` is on the path (inclusive). */
function isDescendantOfOrSelf(schema: Schema, ancestorSchemaId: string, byId: Map<string, Schema>): boolean {
  let current: Schema | undefined = schema;
  const visited = new Set<string>();
  while (current) {
    if (current.schema_id === ancestorSchemaId) return true;
    if (visited.has(current.schema_id)) return false;
    visited.add(current.schema_id);
    current = current.parent_schema_id ? byId.get(current.parent_schema_id) : undefined;
  }
  return false;
}

export interface SphereFamilyTypePickerMoleculeProps {
  /** Full schema set to pick families/types from — this molecule computes the scope, it doesn't consume one. */
  schemas: Schema[];
  /** Abstract-ancestor depth for the family axis (see getSchemaCategories). */
  categoryDepth?: number;
  locale?: string;
  /**
   * The level/segment's full `propertyFilters` — only the `label`/`type` entries are
   * read and written; every other entry passes through `onChange` unchanged.
   */
  value?: FilterExpression[];
  onChange?: (filters: FilterExpression[]) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  readOnly?: boolean;
  className?: string;
}

const SphereFamilyTypePickerMolecule: React.FC<SphereFamilyTypePickerMoleculeProps> = ({
  schemas,
  categoryDepth = 1,
  locale = 'en-US',
  value = [],
  onChange,
  collapsible = true,
  defaultCollapsed = true,
  readOnly = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const byId = useMemo(() => new Map(schemas.map((s) => [s.schema_id, s])), [schemas]);
  const items = useMemo(() => getEligibleSchemaObjects(schemas), [schemas]);
  const families = useMemo(
    () => getSchemaCategories(items, schemas, categoryDepth, locale),
    [items, schemas, categoryDepth, locale]
  );

  const familyFilter = value.find((f) => f.property === 'label');
  const typeFilter = value.find((f) => f.property === 'type');
  const selectedFamilies = filterValues(familyFilter);
  const selectedTypes = filterValues(typeFilter);

  const visibleItems = useMemo(() => {
    if (selectedFamilies.length === 0) return items;
    const familyIds = families.filter((f) => selectedFamilies.includes(f.system_name)).map((f) => f.schema_id);
    return items.filter((item) => familyIds.some((id) => isDescendantOfOrSelf(item.schema, id, byId)));
  }, [items, families, selectedFamilies, byId]);

  const emit = useCallback(
    (nextFamilies: string[], nextTypes: string[]) => {
      const others = value.filter((f) => f.property !== 'label' && f.property !== 'type');
      const next: FilterExpression[] = [...others];
      if (nextFamilies.length > 0) next.push({ property: 'label', operator: 'in', values: nextFamilies });
      if (nextTypes.length > 0) next.push({ property: 'type', operator: 'in', values: nextTypes });
      onChange?.(next);
    },
    [value, onChange]
  );

  const toggleFamily = useCallback(
    (systemName: string) => {
      const next = selectedFamilies.includes(systemName)
        ? selectedFamilies.filter((n) => n !== systemName)
        : [...selectedFamilies, systemName];
      emit(next, selectedTypes);
    },
    [selectedFamilies, selectedTypes, emit]
  );

  const toggleType = useCallback(
    (systemName: string) => {
      const next = selectedTypes.includes(systemName)
        ? selectedTypes.filter((n) => n !== systemName)
        : [...selectedTypes, systemName];
      emit(selectedFamilies, next);
    },
    [selectedFamilies, selectedTypes, emit]
  );

  const activeCount = selectedFamilies.length + selectedTypes.length;

  return (
    <div className={`sphere-family-type-picker-molecule ${className}`}>
      {collapsible && (
        <button
          type="button"
          className="sphere-family-type-picker-molecule__header"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="sphere-family-type-picker-molecule__header-content">
            <i className="pi pi-th-large" />
            <span className="sphere-family-type-picker-molecule__title">
              {t('context.editor.familyTypePicker.title', { defaultValue: 'Sphere Family / Type' })}
            </span>
            {activeCount > 0 && <span className="sphere-family-type-picker-molecule__badge">{activeCount}</span>}
          </div>
          <i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} />
        </button>
      )}

      {(!collapsible || !collapsed) && (
        <div className="sphere-family-type-picker-molecule__content">
          {families.length > 0 && (
            <div className="sphere-family-type-picker-molecule__section">
              <div className="sphere-family-type-picker-molecule__section-label">
                {t('context.editor.familyTypePicker.familyLabel', { defaultValue: 'Family' })}
              </div>
              <div className="sphere-family-type-picker-molecule__pill-row">
                {families.map((family) => {
                  const active = selectedFamilies.includes(family.system_name);
                  return (
                    <button
                      key={family.schema_id}
                      type="button"
                      className={`sphere-family-type-picker-molecule__pill${active ? ' sphere-family-type-picker-molecule__pill--active' : ''}`}
                      onClick={() => !readOnly && toggleFamily(family.system_name)}
                      disabled={readOnly}
                      aria-pressed={active}
                      data-testid={`family-pill-${family.system_name}`}
                    >
                      {family.schema_name_translations?.[locale] ?? family.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="sphere-family-type-picker-molecule__section">
            <div className="sphere-family-type-picker-molecule__section-label">
              {t('context.editor.familyTypePicker.typeLabel', { defaultValue: 'Type' })}
            </div>
            <div className="sphere-family-type-picker-molecule__type-grid">
              {visibleItems.map((item) => {
                const active = selectedTypes.includes(item.schema.system_name);
                return (
                  <button
                    key={item.schema.schema_id}
                    type="button"
                    className={`sphere-family-type-picker-molecule__type-item${active ? ' sphere-family-type-picker-molecule__type-item--active' : ''}`}
                    onClick={() => !readOnly && toggleType(item.schema.system_name)}
                    disabled={readOnly}
                    aria-pressed={active}
                    data-testid={`type-item-${item.schema.system_name}`}
                  >
                    {item.schema.schema_name_translations?.[locale] ?? item.schema.name}
                  </button>
                );
              })}
              {visibleItems.length === 0 && (
                <span className="sphere-family-type-picker-molecule__empty">
                  {t('context.editor.familyTypePicker.noTypes', { defaultValue: 'No types in this family' })}
                </span>
              )}
            </div>
          </div>

          {activeCount > 0 && !readOnly && (
            <div className="sphere-family-type-picker-molecule__selected">
              {selectedFamilies.map((name) => {
                const family = families.find((f) => f.system_name === name);
                return (
                  <ChipAtom
                    key={`family-${name}`}
                    label={family?.name ?? name}
                    icon="pi pi-sitemap"
                    removable
                    onRemove={() => {
                      toggleFamily(name);
                      return false;
                    }}
                    className="sphere-family-type-picker-molecule__selected-chip"
                  />
                );
              })}
              {selectedTypes.map((name) => {
                const item = items.find((i) => i.schema.system_name === name);
                return (
                  <ChipAtom
                    key={`type-${name}`}
                    label={item?.schema.name ?? name}
                    removable
                    onRemove={() => {
                      toggleType(name);
                      return false;
                    }}
                    className="sphere-family-type-picker-molecule__selected-chip"
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SphereFamilyTypePickerMolecule;
